import type { FC } from 'react';
import { memo, useCallback, useRef } from 'react';
import isEqual from 'lodash/isEqual';

import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { addUFOCustomMark } from '@atlaskit/react-ufo/custom-mark';

import { VIEW_BLOG, VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import { usePerformanceAction } from '../usePerformanceAction.tsx';
import { PAGE_SEGMENT_MARK_PREFIX_END } from '../metric-keys/index.ts';
import { browserMetricsState } from '../utils/index.ts';
import { getViewTransitionSource } from '../viewTransitionSource.ts';
import type { Props } from '../types/PageSegmentLoadEndProps.ts';

import { addCustomTimingForMetric } from './addCustomTimingsFromMarks.ts';
import { useIsEmbeddedTracking } from './EmbeddedConfluenceTrackingContext.tsx';

const noop = () => {};
const EMBEDDED_PREFIX = 'embedded-';

const UFOSegmentLoadEndComponent: FC<Props> = memo(
	({
		customData = {},
		metric,
		onComplete = noop,
		stopTime,
		timing,
		augmentCustomDataAfterComplete,
	}) => {
		const isEmbedded = useIsEmbeddedTracking();
		const prefixedKey = isEmbedded ? `${EMBEDDED_PREFIX}${metric.key}` : metric.key;

		const stopTimeRef = useRef(stopTime);
		const customDataRef = useRef(customData);
		const augmentCustomDataAfterCompleteRef = useRef(augmentCustomDataAfterComplete);

		stopTimeRef.current = stopTime;
		customDataRef.current = customData;
		augmentCustomDataAfterCompleteRef.current = augmentCustomDataAfterComplete;

		// Intentional use that won't cause a hydration error
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			SSRMeasures.markMeasureEnd(`ssr-app/render/${prefixedKey}`);
		}

		const handleSegmentLoadEnd = useCallback(() => {
			if (metric) {
				onComplete();
				let finalCustomData: Record<string, any>;
				if (augmentCustomDataAfterCompleteRef.current) {
					finalCustomData = augmentCustomDataAfterCompleteRef.current(customDataRef.current);
				} else {
					finalCustomData = customDataRef.current;
				}

				const { route } = metric.getData();

				const shouldAddViewTransitionSource = route === VIEW_PAGE.name || route === VIEW_BLOG.name;
				if (shouldAddViewTransitionSource) {
					finalCustomData.viewTransitionSource = getViewTransitionSource();
				}

				addUFOCustomMark(`${PAGE_SEGMENT_MARK_PREFIX_END}:${prefixedKey}`);

				addCustomTimingForMetric(prefixedKey);

				addUFOCustomData({
					...finalCustomData,
					...(typeof stopTimeRef.current === 'number' ? { stopTime: stopTimeRef.current } : {}),
					previousRoute: browserMetricsState.previousRoute,
					previousContentId: browserMetricsState.previousContentId,
					pageLoadInfo: getUniquePageLoadId(),
				});
			}
		}, [metric, onComplete, prefixedKey]);

		usePerformanceAction(handleSegmentLoadEnd, timing);
		return null;
	},
	isEqual,
);

export const UFOSegmentLoadEnd = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(UFOSegmentLoadEndComponent);
